import React from 'react';
import { Field, useFormikContext } from 'formik';

import {
  InputUI,
  FormErrorUI,
  FormLabelUI,
  CheckboxUI,
  AutoCompleteSelectUI,
  ButtonUI,
  SelectUI,
} from '../../Interface';
import { AlertTypeEnum, AlertTypeList } from '../../../config/alert';
import { DashboardPdfExportModeEnum, DashboardPdfExportModeList } from '../../../config/dashboard';

const AlertFormFields = ({ viewField, autocompleteOptions, autocompleteData, openAlertFormDialog }) => {
  const { values, errors, setFieldValue, validateField } = useFormikContext();

  const handleFieldChange = async (field, newVal) => {
    const selected = newVal?.value ?? null;
    await setFieldValue(field, selected);
    await validateField(field);
  };

  const handleTypeChange = async (event) => {
    const type = event.target.value;
    await setFieldValue('type', type);

    if (type === AlertTypeEnum.ALERT) {
      await setFieldValue('dashboardId', null);
      await setFieldValue('dashboard', null);
      await setFieldValue('pdfExportMode', null);
    } else if (type === AlertTypeEnum.SCHEDULED_TASK) {
      await setFieldValue('dashboardId', null);
      await setFieldValue('dashboard', null);
      await setFieldValue('pdfExportMode', null);
      await setFieldValue('alertRecipient', []);
      await setFieldValue('alertEmailRecipient', []);
    } else if (type === AlertTypeEnum.DASHBOARD_EMAIL) {
      await setFieldValue('view', null);
      await setFieldValue('alertRecipient', []);
      await setFieldValue('pdfExportMode', values.pdfExportMode ?? DashboardPdfExportModeEnum.FULL);
    }
  };

  const viewOptions =
    values.type === AlertTypeEnum.SCHEDULED_TASK ? autocompleteOptions.procedureViews : autocompleteOptions.views;

  return (
    <div className="grid grid-cols-4 gap-x-4 mx-4 mb-5">
      <div className="md:col-span-2 col-span-4">
        <Field
          name="type"
          as={SelectUI}
          option={AlertTypeList}
          value={values.type ?? AlertTypeEnum.ALERT}
          label={<FormLabelUI text="Típus" />}
          helperText={<FormErrorUI message={errors.type} />}
          disabled={viewField}
          onChange={handleTypeChange}
        />
      </div>

      <div className="md:col-span-2 col-span-4">
        <Field
          type="text"
          name="name"
          as={InputUI}
          fullWidth
          label={<FormLabelUI text="Riasztás neve" />}
          helperText={<FormErrorUI message={errors.name} />}
          variant="standard"
          InputLabelProps={{ shrink: values.name && values.name !== '' }}
          disabled={viewField}
        />
      </div>

      {values.type !== AlertTypeEnum.DASHBOARD_EMAIL ? (
        <div className="md:col-span-2 col-span-4">
          <Field
            type="autocomplete"
            name="view"
            component={AutoCompleteSelectUI}
            fullWidth
            label={<FormLabelUI text="Riasztás forrása" />}
            variant="standard"
            helperText={<FormErrorUI message={errors.view} />}
            onChange={async (_e, newVal) => handleFieldChange('view', newVal)}
            disabled={viewField}
            InputLabelProps={{ shrink: Boolean(values.view) }}
            selectedValue={values.view}
            selectedLabelValue={viewOptions.find((item) => item.value === values.view)?.label || ''}
            dataset={viewOptions}
          />
        </div>
      ) : null}

      {values.type === AlertTypeEnum.DASHBOARD_EMAIL ? (
        <div className="md:col-span-2 col-span-4">
          <Field
            type="autocomplete"
            name="dashboardId"
            component={AutoCompleteSelectUI}
            fullWidth
            label={<FormLabelUI text="Dashboard" />}
            variant="standard"
            helperText={<FormErrorUI message={errors.dashboardId} />}
            onChange={async (_event, newValue) => {
              const dashboardId = newValue?.value ?? null;
              await setFieldValue('dashboardId', dashboardId);
              await setFieldValue(
                'dashboard',
                autocompleteData.dashboards.find((dashboard) => dashboard.id === dashboardId) ?? null
              );
              await validateField('dashboardId');
            }}
            disabled={viewField}
            InputLabelProps={{ shrink: Boolean(values.dashboardId) }}
            selectedValue={values.dashboardId}
            selectedLabelValue={
              autocompleteOptions.dashboards.find((item) => item.value === values.dashboardId)?.label ??
              values.dashboard?.name ??
              ''
            }
            dataset={autocompleteOptions.dashboards}
          />
        </div>
      ) : null}

      {values.type === AlertTypeEnum.DASHBOARD_EMAIL ? (
        <div className="md:col-span-2 col-span-4">
          <Field
            name="pdfExportMode"
            as={SelectUI}
            option={DashboardPdfExportModeList}
            value={values.pdfExportMode ?? DashboardPdfExportModeEnum.FULL}
            label={<FormLabelUI text="PDF riport típusa" />}
            helperText={<FormErrorUI message={errors.pdfExportMode} />}
            disabled={viewField}
          />
        </div>
      ) : null}

      <div className="md:col-span-2 col-span-4">
        <Field
          name="active"
          type="checkbox"
          as={CheckboxUI}
          fullWidth
          label={<FormLabelUI text="Aktív" />}
          disabled={viewField}
        />
      </div>

      <div className="md:col-span-2 col-span-4">
        <ButtonUI
          text="Részletek"
          size="sm"
          className="bg-labelColor py-3"
          onClick={() => openAlertFormDialog()}
          disabled={viewField}
        />
      </div>
    </div>
  );
};

export default AlertFormFields;
